<template>
  <search class="relative">
    <div class="mb-2 w-full md:w-100 mx-auto md:ml-auto md:mr-0 lg:mx-auto relative">
      <font-awesome-icon :icon="['far', 'search']" class="absolute text-gray-500 mt-3 left-14px" />
      <form>
        <input
          ref="searchInput"
          v-model="searchTerm"
          class="w-full outline-none p-2 pl-14 text-gray-600 shadow rounded-sm"
          :placeholder="$t('Search.StudentOrSection')"
          aria-describedby="search-results"
          @keyup="onKeyUp"
        />
        <button
          :aria-label="$t('Search.Clear')"
          class="absolute mt-1 text-xl text-gray-600 right-14px outline-button w-7 h-7"
          @click.prevent="cancelSearch"
        >
          <font-awesome-icon :icon="['fal', 'times']" />
        </button>
      </form>
    </div>

    <div
      id="search-results"
      class="nc-card absolute z-10 w-full opacity-97 mb-10"
      style="max-width: 1000px; left: 50%; transform: translateX(calc(-50% - 1px))"
    >
      <div v-if="sectionsResultFromSearch.length">
        <h2 class="ml-5 my-2">{{ $t("Sections.Section.Section") }}</h2>

        <section-table
          :table-caption="$t('Sections.Sections')"
          :sections="sectionsResultFromSearch"
          :disabled-section-ids="disabledSectionIds"
          :is-section-selectable="false"
          :is-section-clickable="true"
          @sectionClick="onSectionClick"
        >
        </section-table>
      </div>

      <div v-if="peopleResultFromSearch.length">
        <h2 class="ml-5 mt-6 mb-2">{{ $t("Sections.Section.Students") }}</h2>

        <people-table
          :table-caption="$t('People.Roles.Students')"
          :students="peopleResultFromSearch"
          :show-groups="false"
          :is-people-selectable="false"
          :is-people-clickable="true"
          on-click-route-name="confidentialsChildView"
          :on-click-route-params="{ childId: ':userId', sectionId: ':sectionId' }"
        >
          <template #column2Header> {{ $t("Sections.Section.Section") }} </template>

          <template #column2Content="props">
            {{ props.person.sectionName }}
          </template>
        </people-table>
      </div>

      <div
        v-if="searchTerm && !sectionsResultFromSearch.length && !peopleResultFromSearch.length"
        class="w-full text-center text-xs my-4"
        aria-live="assertive"
        aria-atomic="true"
      >
        {{ $t("Search.NoResults") }}
      </div>
    </div>
  </search>
</template>

<script>
import { useSectionsStore } from "@/stores/sections";
import { useMeStore } from "@/stores/me";
import { usePeopleStore } from "@/stores/people";
import PeopleTable from "@/components/People/PeopleTable";
import SectionTable from "@/components/Section/SectionTable";
const cprRules = /^[0-9]{6}-?[0-9]{4}$/;
export default {
  name: "ConfidentialPeopleSearch",
  data: function () {
    return {
      searchTerm: null,
      disabledPeopleIds: [],
      disabledSectionIds: []
    };
  },

  props: {
    groupId: { type: String, default: null },
    level: { type: String, default: "0" }
  },

  setup() {
    return {
      sectionsStore: useSectionsStore(),
      meStore: useMeStore(),
      peopleStore: usePeopleStore()
    };
  },

  mounted: function () {
    this.loadSections();
  },

  watch: {
    searchTerm(newVal) {
      if (cprRules.test(newVal)) {
        this.peopleStore.cprSearch(newVal);
      }
    }
  },

  computed: {
    sectionsResultFromSearch() {
      if (this.searchTerm && this.searchTerm.length) {
        return this.sectionsStore.mySectionsFromSearch(this.searchTerm);
      }
      return [];
    },

    peopleResultFromSearch() {
      if (this.searchTerm && this.searchTerm.length) {
        const isCpr = cprRules.test(this.searchTerm);
        const cprResults = isCpr && this.peopleStore.peopleCpr ? [].concat(this.peopleStore.peopleCpr) : [];

        const people = [
          ...this.sectionsStore.allStudentsAsPeopleTableObjectsFromSectionsSearched(this.searchTerm.toLowerCase()),
          ...(this.searchTerm.length > 1
            ? this.sectionsStore.allMyStudentsSearchedAsPeopleTableObejcts(this.searchTerm.toLowerCase())
            : []),
          ...cprResults
        ];
        return people.filter((obj, index, self) => index === self.findIndex(t => t.userId === obj.userId));
      }
      return [];
    }
  },

  methods: {
    async loadSections() {
      await this.sectionsStore.loadMySections();
      this.sectionsStore.loadAllSectionsOnInstitution();
    },

    async onSectionClick(clickedSection) {
      this.$router.push({
        name: "confidentialsSectionView",
        params: {
          sectionId: clickedSection.sectionId
        }
      });
    },

    async cancelSearch() {
      this.searchTerm = null;
      this.$refs.searchInput.focus();
    },

    onKeyUp(val) {
      if (val.keyCode == 27) {
        this.cancelSearch();
      }
    }
  },

  components: {
    PeopleTable,
    SectionTable
  }
};
</script>

<style scoped>
.nc-card {
  padding: 0rem;
}

.nc-card-no-results {
  padding: 1rem;
}

@media (min-width: 1280px) {
  .child {
    left: calc(-10vw);
    width: 50vw;
  }
}
</style>
